import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation, Link } from 'react-router-dom';

import { parse, stringify, createSlug } from '../../util/urlHelpers';
import { parseSelectFilterOptions } from '../../util/search';
import { makeGetListingsByIdSelector } from '../../ducks/marketplaceData.duck';
import { useConfiguration } from '../../context/configurationContext';
import { searchListings } from '../SearchPage/SearchPage.duck';
import { Page } from '../../components';
import DhobaraSiteNav from '../../components/DhobaraSiteNav/DhobaraSiteNav';
import DhobaraFooter from '../../components/DhobaraFooter/DhobaraFooter';

import { getListingEnumOptions, getListingLabelMap } from '../../config/configListing';
import { isSoldOut } from '../../util/soldOut';
import css from './DhobaraSearchPage.module.css';

// ── Static lookup tables ───────────────────────────────────────────────────────
//
// Dhobara: these are DERIVED from src/config/configListing.js, which is the
// single source of truth for what a seller can pick. This file used to keep
// verbatim copies of all seven lists, and they drifted from the real options
// over time — a designer with the wrong label, a designer missing entirely, a
// colour that was never copied across. Add an option in configListing.js and
// it shows up here automatically.

const DESIGNER_OPTIONS = getListingEnumOptions('designer');
const DESIGNER_LABEL_MAP = getListingLabelMap('designer');

const COLLECTION_OPTIONS = getListingEnumOptions('collection');
const COLLECTION_LABEL_MAP = getListingLabelMap('collection');

const COLOR_OPTIONS = getListingEnumOptions('color');
const FABRIC_OPTIONS = getListingEnumOptions('fabric');
const CONDITION_OPTIONS = getListingEnumOptions('condition');
const SIZE_OPTIONS = getListingEnumOptions('size');
const GARMENT_OPTIONS = getListingEnumOptions('garmentType');

// ── Listing card ───────────────────────────────────────────────────────────────

function ListingCard({ listing }) {
  const attrs = listing.attributes || {};
  const title = attrs.title || '';
  const price = attrs.price;
  const pub = attrs.publicData || {};
  const designerSlug = pub.designer;
  const designerLabel = DESIGNER_LABEL_MAP[designerSlug] || designerSlug || '';
  const collection = pub.collection;
  const garmentTypes = Array.isArray(pub.garmentType) ? pub.garmentType : [];
  const priceStr = price
    ? `$${(price.amount / 100).toLocaleString('en-US', { minimumFractionDigits: 0 })}`
    : null;

  const images = listing.images || [];
  const firstImage = images[0];
  const imgUrl =
    firstImage?.attributes?.variants?.['listing-card-2x']?.url ||
    firstImage?.attributes?.variants?.['listing-card']?.url ||
    firstImage?.attributes?.variants?.['scaled-medium']?.url ||
    null;

  // Fallback: if no designer in publicData but title contains "slug — garments" format, parse it
  let displayDesigner = designerLabel;
  let displayTitle = title;
  if (!displayDesigner && title.includes(' — ')) {
    const [slugPart, ...rest] = title.split(' — ');
    const resolved = DESIGNER_LABEL_MAP[slugPart.trim()];
    if (resolved) {
      displayDesigner = resolved;
      displayTitle = rest.join(' — ').trim();
    }
  }

  // Collection pill — only show if collection is set in publicData
  const pillLabel = collection && COLLECTION_LABEL_MAP[collection]
    ? COLLECTION_LABEL_MAP[collection]
    : null;

  const slug = createSlug(title);
  const id = listing.id?.uuid;

  const sold = isSoldOut(listing);

  return (
    <Link to={`/l/${slug}/${id}`} className={css.card}>
      <div className={css.cardImage}>
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={displayDesigner || displayTitle}
            loading="lazy"
            className={sold ? css.cardImageSold : undefined}
          />
        ) : (
          <div className={css.cardImagePlaceholder} />
        )}
        {sold && <span className={css.soldBadge}>Sold</span>}
      </div>
      {displayDesigner && <p className={css.cardDesigner}>{displayDesigner.toUpperCase()}</p>}
      <p className={css.cardGarment}>{displayTitle}</p>
      <div className={css.cardMeta}>
        {priceStr && <span className={css.cardPrice}>{priceStr}</span>}
        {pillLabel && (
          <span className={css.cardCollection}>
            {pillLabel.toUpperCase()}
          </span>
        )}
      </div>
    </Link>
  );
}

// ── Filter sidebar ─────────────────────────────────────────────────────────────

function FilterSelect({ label, value, options, onChange }) {
  return (
    <div className={css.filterGroup}>
      <label className={css.filterGroupLabel}>{label}</label>
      <select className={css.filterSelect} value={value} onChange={e => onChange(e.target.value)}>
        <option value="">All</option>
        {options.map(o => (
          <option key={o.option} value={o.option}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// A multi-select filter. Unlike the single <select> above, the options live in
// an inline panel rather than a floating popup: the sidebar is a scrolling
// container, and an absolutely positioned menu would be clipped by it.
// Collapsed by default so eleven garment types don't push the rest of the
// filters off screen, and opened on arrival if the URL already carries a
// selection.
function FilterCheckboxes({ label, values, options, onChange }) {
  const [open, setOpen] = useState(values.length > 0);

  const summary =
    values.length === 0
      ? 'All'
      : values.length === 1
      ? options.find(o => o.option === values[0])?.label || values[0]
      : `${values.length} selected`;

  const toggle = option =>
    onChange(
      values.includes(option) ? values.filter(v => v !== option) : [...values, option]
    );

  return (
    <div className={css.filterGroup}>
      <label className={css.filterGroupLabel}>{label}</label>
      <button
        type="button"
        className={`${css.filterSelect} ${css.filterToggle}`}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        {summary}
      </button>
      {open && (
        <div className={css.checkboxList}>
          {options.map(o => (
            <label key={o.option} className={css.checkboxRow}>
              <input
                type="checkbox"
                className={css.checkbox}
                checked={values.includes(o.option)}
                onChange={() => toggle(o.option)}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

const DhobaraSearchPage = () => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = parse(location.search);

  // Read active filters from URL (sent to Sharetribe API as pub_* params)
  const activeCollection = queryParams.pub_collection || '';
  const activeDesigner = queryParams.pub_designer || '';
  const activeCondition = queryParams.pub_condition || '';
  const activeSize = queryParams.pub_size || '';
  // Multi-enum, so the param is "has_any:kurta,dupatta" — any of the selected
  // pieces, matching how the Collection and Designer pages combine the same
  // filter. parseSelectFilterOptions strips the prefix.
  const garmentParam = queryParams.pub_garmentType || '';
  const activeGarments = useMemo(
    () => (garmentParam ? parseSelectFilterOptions(garmentParam) : []),
    [garmentParam]
  );

  // Color and fabric are filtered client-side only — the Sharetribe search index
  // for these new fields is still propagating, so we keep them out of the API call
  // (stripped in SearchPage.duck.js loadData) and filter locally instead.
  // Initialize from URL params so old bookmarked/shared links still work.
  const [activeColor, setActiveColor] = useState(queryParams.pub_color || '');
  const [activeFabric, setActiveFabric] = useState(queryParams.pub_fabric || '');


  // Redux listing data
  const selectListingsById = useMemo(makeGetListingsByIdSelector, []);
  const { searchInProgress, currentPageResultIds = [], pagination, searchParams } = useSelector(
    state => state.SearchPage || {}
  );
  const allListings = useSelector(state => selectListingsById(state, currentPageResultIds));

  // Client-side filtering for color and fabric — Sharetribe's search index can take time
  // to propagate new schema fields. This guarantees correct filtering even before that happens.
  // Once Sharetribe indexes the fields, the server will pre-filter and client-side just no-ops.
  const listings = useMemo(() => {
    return allListings.filter(listing => {
      const pub = listing.attributes?.publicData || {};
      if (activeColor && pub.color !== activeColor) return false;
      if (activeFabric && pub.fabric !== activeFabric) return false;
      return true;
    });
  }, [allListings, activeColor, activeFabric]);

  function setFilter(key, value) {
    const updated = { ...queryParams };
    if (value) {
      updated[key] = value;
    } else {
      delete updated[key];
    }
    // Remove page param on filter change
    delete updated.page;
    history.push(`/s?${stringify(updated)}`);
  }

  function clearAll() {
    setActiveColor('');
    setActiveFabric('');
    history.push('/s');
  }

  // The grid renders one API page. Show the marketplace's real total when
  // nothing is being filtered client-side, so the header doesn't report the
  // page size as though it were the whole catalogue.
  const clientFiltered = !!(activeColor || activeFabric);
  const count = clientFiltered ? listings.length : pagination?.totalItems ?? listings.length;

  // ── Infinite scroll ────────────────────────────────────────────────────────
  //
  // The API hands back one page at a time. A sentinel below the grid asks for
  // the next one as it comes into view, and the duck appends rather than
  // replaces. The Load More button underneath is the same action for anyone
  // the observer doesn't reach — keyboard users, older browsers, or a screen
  // too tall for the sentinel to ever leave the viewport.
  const dispatch = useDispatch();
  const config = useConfiguration();
  const sentinelRef = useRef(null);

  const loadedPage = pagination?.page || 1;
  const totalPages = pagination?.totalPages || 1;
  const hasMore = loadedPage < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMore || searchInProgress || !searchParams) return;
    dispatch(
      searchListings({
        searchParams: { ...searchParams, page: loadedPage + 1 },
        config,
      })
    );
  }, [hasMore, searchInProgress, searchParams, loadedPage, dispatch, config]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) loadMore();
      },
      // Start fetching a little before the sentinel is actually on screen, so
      // the next row is usually there by the time the reader arrives.
      { rootMargin: '600px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);


  return (
    <Page
      title="Browse — Dhobara"
      description="Browse all authenticated South Asian designer pieces on Dhobara."
      scrollingDisabled={false}
    >
      <div className={css.page}>
        <DhobaraSiteNav />
        <main className={css.main}>
          <div className={css.pageHeader}>
            <p className={css.eyebrow}>SHOP ALL</p>
            <h1 className={css.heading}>Browse the Collection</h1>
          </div>

          <div className={css.layout}>
            {/* ── Sidebar ── */}
            <aside className={css.sidebar}>
              <div className={css.sidebarHeader}>
                <span className={css.sidebarTitle}>FILTERS</span>
                <button type="button" className={css.clearBtn} onClick={clearAll}>
                  CLEAR
                </button>
              </div>

              <FilterSelect
                label="COLLECTION"
                value={activeCollection}
                options={COLLECTION_OPTIONS}
                onChange={v => setFilter('pub_collection', v)}
              />

              <FilterSelect
                label="DESIGNER"
                value={activeDesigner}
                options={DESIGNER_OPTIONS}
                onChange={v => setFilter('pub_designer', v)}
              />

              <FilterSelect
                label="CONDITION"
                value={activeCondition}
                options={CONDITION_OPTIONS}
                onChange={v => setFilter('pub_condition', v)}
              />

              <FilterSelect
                label="SIZE"
                value={activeSize}
                options={SIZE_OPTIONS}
                onChange={v => setFilter('pub_size', v)}
              />

              <FilterCheckboxes
                label={"WHAT'S INCLUDED"}
                values={activeGarments}
                options={GARMENT_OPTIONS}
                onChange={vals =>
                  setFilter('pub_garmentType', vals.length ? `has_any:${vals.join(',')}` : '')
                }
              />

              <FilterSelect
                label="COLOR"
                value={activeColor}
                options={COLOR_OPTIONS}
                onChange={v => setActiveColor(v)}
              />

              <FilterSelect
                label="FABRIC"
                value={activeFabric}
                options={FABRIC_OPTIONS}
                onChange={v => setActiveFabric(v)}
              />
            </aside>

            {/* ── Listing grid ── */}
            <section className={css.results}>
              <div className={css.resultsHeader}>
                <p className={css.resultsCount}>
                  {searchInProgress && listings.length === 0
                    ? 'Loading…'
                    : `${count} ${count === 1 ? 'PIECE' : 'PIECES'}`}
                </p>
              </div>

              {!searchInProgress && count === 0 ? (
                <div className={css.empty}>
                  <h3 className={css.emptyHeading}>No pieces match your filters</h3>
                  <p className={css.emptyBody}>
                    Try clearing a filter or browsing a different collection.
                  </p>
                  <button type="button" className={css.emptyBtn} onClick={clearAll}>
                    Clear Filters
                  </button>
                </div>
              ) : (
                <>
                  <div className={css.grid}>
                    {listings.map(listing => (
                      <ListingCard key={listing.id?.uuid} listing={listing} />
                    ))}
                  </div>

                  {hasMore && (
                    <div className={css.loadMore} ref={sentinelRef}>
                      {searchInProgress ? (
                        <p className={css.loadingMore}>Loading more pieces…</p>
                      ) : (
                        <button type="button" className={css.loadMoreBtn} onClick={loadMore}>
                          Load More
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        </main>

        <DhobaraFooter />
      </div>
    </Page>
  );
};

export default DhobaraSearchPage;
